<template>
  <div class="consumer-mypage-edit" v-if="userData">
    <h1>비밀번호 변경</h1>
    <div class="user-info-container">
      <div class="summary-container">
        <div style="width: 50%" class="user-container">
          <span class="material-icons-outlined"> person </span>
          <div>
            <h2>안녕하세요. {{ userData.userName }} 님!</h2>
            <p>
              회원님의 개인정보를 안전하게 보호하고, 개인정보 도용으로 인한
              피해를 예방하기 위해 90일 이상 비밀번호를 변경하지 않은 경우
              비밀번호 변경을 권장하고 있습니다.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="edit-container">
      <div class="input-container">
        <div>
          <p>현재 비밀번호</p>
          <div>
            <input type="password" v-model="existPassword" maxlength="16" />
          </div>
        </div>
        <div>
          <p>새로운 비밀번호</p>
          <div>
            <input type="password" v-model="newPassword" maxlength="16" />
          </div>
        </div>
        <div>
          <p>새로운 비밀번호 다시 입력</p>
          <div>
            <input type="password" v-model="confirmPassword" maxlength="16" />
          </div>
        </div>
      </div>
    </div>
    <div class="button-container">
      <button class="outlined" @click="goBack">취소</button>
      <button class="primary" @click="updateUserData">변경내용 저장</button>
    </div>
  </div>
</template>

<script setup lang="js">
import { onMounted, ref } from "vue";
import { db } from "@/lib/firebase";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import router from "@/router";
import { getUserId } from "@/lib/auth";
import { decrypt, encrypt } from "@/lib/crypto";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const userData = ref(null);
const existPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");

const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-={}[\]|\\:;"'<>,.?/]).{8,16}$/;
// ↑ 기존 정책이 "영문+숫자"만이면 아래 검사로 바꿔도 됨
// /^(?=.*[a-zA-Z])(?=.*\d).{8,16}$/

const updateUserData = () =>
  runLocked(async () => {
    if (!userData.value) {
      alert("회원 정보를 불러오지 못했습니다.");
      return;
    }

    // 1) 필수값 체크
    if (
      existPassword.value.trim() === "" ||
      newPassword.value.trim() === "" ||
      confirmPassword.value.trim() === ""
    ) {
      alert("모든 항목을 입력하세요.");
      return;
    }

    // 2) 기존 비밀번호 확인
    const decryptedPassword = await decrypt(userData.value.userPassword);
    if (decryptedPassword !== existPassword.value) {
      alert("기존 비밀번호가 일치하지 않습니다.");
      return;
    }

    // 3) 새 비밀번호 유효성 검사
    if (!passwordRegex.test(newPassword.value)) {
      alert(
        "비밀번호는 8~16자이며, 영문 대/소문자, 숫자, 특수문자를 포함해야 합니다."
      );
      return;
    }

    // 정책이 영문+숫자만이면 아래로 대체
    // if (newPassword.value.length < 8 || newPassword.value.length > 16) {
    //   alert("비밀번호는 8~16자로 입력하세요.");
    //   return;
    // }
    // if (!/[a-zA-Z]/.test(newPassword.value) || !/[0-9]/.test(newPassword.value)) {
    //   alert("비밀번호는 영문, 숫자를 포함해야 합니다.");
    //   return;
    // }

    // 4) 새 비밀번호 확인 일치
    if (newPassword.value !== confirmPassword.value) {
      alert("새로운 비밀번호가 일치하지 않습니다.");
      return;
    }

    // 5) 기존/새 비밀번호 동일 방지 (선택)
    if (existPassword.value === newPassword.value) {
      alert("새 비밀번호는 기존 비밀번호와 다르게 입력해주세요.");
      return;
    }

    const uid = getUserId();
    const userRef = doc(db, "users", uid);

    const encryptedPassword = await encrypt(newPassword.value);

    // ✅ 비밀번호 필드만 업데이트 (안전)
    await updateDoc(userRef, {
      userPassword: encryptedPassword,
    });

    // 로컬 상태도 반영
    userData.value.userPassword = encryptedPassword;

    alert("비밀번호가 변경되었습니다!");
    window.location.href = "/mypage";
  }).catch((error) => {
    console.error("Error updating user data:", error);
    alert("비밀번호 변경 중 오류가 발생했습니다.");
  });

function goBack() {
  router.back();
}

const fetchUserData = () =>
  runLocked(async () => {
    const uid = getUserId();
    const userSnap = await getDoc(doc(db, "users", uid));

    if (!userSnap.exists()) {
      alert("회원 정보를 찾을 수 없습니다.");
      return;
    }

    userData.value = userSnap.data();
  }).catch((error) => {
    console.error("Failed to fetch user data:", error);
    alert("회원 정보를 불러오지 못했습니다.");
  });

onMounted(() => {
  fetchUserData();
});
</script>

<style lang="scss" scoped>
.consumer-mypage-edit {
  padding: 16px 24px;
  margin: auto;
  max-width: 1200px;
  min-height: 70vh;

  > h1 {
    text-align: center;
  }

  > .user-info-container {
    margin-top: 48px;
    > div {
      border: 1px solid #efefef;
      padding: 48px 24px;
      width: 100%;
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;

      &:not(:first-child) {
        border-top: none;
      }
    }

    > .summary-container {
      > .user-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        * {
          text-align: center;
        }
        > span {
          font-size: 48px;
          border: 4px solid black;
          height: fit-content;
          border-radius: 50%;
          margin-bottom: 16px;
        }

        > div {
          > p {
            margin-top: 8px;
            font-size: 16px;
          }
        }
      }
    }
  }

  > .edit-container {
    margin-top: 48px;
    > .title-container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      > p {
        font-size: 14px;
        > span {
          font-weight: 700;
          color: #007bff;
          margin-right: 4px;
        }
      }
    }

    > .input-container {
      border-top: 2px solid black;
      margin-top: 16px;
      > div {
        display: flex;
        align-items: stretch;
        &:not(:first-child) {
          border-top: 1px solid #ccc;
        }
        > p {
          padding: 12px 16px;
          width: 240px;
          background: #efefef;
          display: flex;
          align-items: center;
        }

        > div {
          flex: 1;
          padding: 12px 16px;

          > input {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #999;
            border-radius: 4px;
            box-sizing: border-box;
            outline: none;
            font-size: 16px;
          }

          > p {
            margin-top: 8px;
            font-size: 14px;
            color: #666;
          }
        }
      }
    }
  }

  > .button-container {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 48px;

    > button {
      flex: 1;
      font-size: 16px;
      cursor: pointer;
      height: 48px;

      &.outlined {
        background: none;
        border: 1px solid #999;
      }

      &.primary {
        background: black;
        color: white;
        font-weight: 700;
        border: none;
      }
    }
  }
}
</style>
